import { useMemo } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { DataTable } from "@unified-checkout/design-system";
import type {
  IColumnsMap,
  IFiltersMap
} from "libs/design-system/src/types/data_table";

import { MOBILE_BREAKPOINT } from "@app/dashboard/constants/break-points";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { useLangStore } from "@app/dashboard/store/lang";
import { useScreenWidth } from "@app/dashboard/hooks/useScreenWidth";
import { getCountryFromDomain } from "@app/dashboard/utils/getCountryFromDomain";
import { INNER_ROUTES } from "@app/dashboard/constants/inner-routes";
import { getDisputes } from "@app/dashboard/services/inner_layout/disputes/listing";
import { statusBadgePropsMap } from "../utils";


const TABLE_ID = 'disputes_table';


export const Table = (
  {
    dateRange,
    isTableLoading,
    setIsTableLoading
  }
    :
    {
      dateRange: { created_at_from: string, created_at_to: string },
      isTableLoading: boolean,
      setIsTableLoading: Dispatch<SetStateAction<boolean>>
    }
) => {
  const navigate = useNavigate();
  const screenWidth = useScreenWidth();
  const { lang } = useLangStore();
  const { t } = useTranslation();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;


  const filtersMap: IFiltersMap = useMemo(() => {

    const filtersList = t('innerLayout.disputesPage.table.filters.status', { returnObjects: true });
    const filtersValues = filtersList.map((filter: any) => ({ [filter.label]: filter.value }));

    const textFieldValidations = [{
      regex: '^\\d+$',
      message: t('errors.numbersOnly'),
    }, {
      regex: '^.{1,15}$',
      message: t('errors.maxLength', { max: 15 }),
    }];

    return [
      {
        type: 'select',
        key: 'dispute_status',
        name: t('innerLayout.disputesPage.table.filters.labels.status'),
        values: filtersValues as Record<string, string>[],
        defaultSelection: 'all'
      },
      {
        type: 'text',
        key: 'transaction_id',
        name: t('innerLayout.disputesPage.table.filters.labels.transactionId'),
        values: '',
        validations: textFieldValidations
      },
      {
        type: 'text',
        key: 'dispute_id',
        name: t('innerLayout.disputesPage.table.filters.labels.disputeId'),
        values: '',
        validations: textFieldValidations
      },
    ]
  }, [t]);

  const columnsMap: IColumnsMap = [
    {
      id: 'dispute_id',
      name: t('innerLayout.disputesPage.table.headers.disputeId'),
      type: 'text',
      key: "id",
    },
    {
      id: 'transaction_id',
      name: t('innerLayout.disputesPage.table.headers.transactionId'),
      type: 'text',
      key: "transaction_id",
    },
    {
      id: 'amount',
      name: t('innerLayout.disputesPage.table.headers.disputeAmount'),
      type: 'amount_cents',
      key: "amount_cents",
      additionalText: { key: "currency", placement: lang === 'en' ? "start" : "end" }
    },
    {
      id: 'reason',
      name: t('innerLayout.disputesPage.table.headers.reason'),
      type: 'text',
      key: "reason_label",
    },
    {
      id: 'date',
      name: t('innerLayout.disputesPage.table.headers.dateCreated'),
      type: 'date',
      key: "created_at",
      hintText: "created_at",
    },
    {
      id: 'due_date',
      name: t('innerLayout.disputesPage.table.headers.dueDate'),
      type: 'date',
      key: "due_date",
      includeTime: false
    },
    {
      id: 'status',
      name: t('innerLayout.disputesPage.table.headers.status'),
      type: 'badge',
      key: "status_label",
      labelComparingValue: "status",
      label: statusBadgePropsMap,
    },
  ];

  const queryKeys = useMemo(
    () => [QUERY_KEYS.GET_DISPUTES, dateRange],
    [dateRange]
  );

  const queryFunctionParams = useMemo(
    () => ({
      ...dateRange
    }),
    [dateRange]
  );


  const handleTableRowClick = (rowInfo: any) => {
    rowInfo && navigate(INNER_ROUTES.SINGLE_DISPUTE.replace(':id', rowInfo.id));
  };


  return (
    <DataTable
      lang={lang}

      filters={{
        filtersMap: filtersMap,
        isMobile: isMobile,
        country: getCountryFromDomain(),
        localization: {
          all: t('innerLayout.all'),
          none: t('innerLayout.none'),
          selected: t('innerLayout.selected'),
          resetFilters: t('innerLayout.resetFilters')
        }
      }}

      tableBody={{
        lang: lang,
        tableId: TABLE_ID,
        dataLocation: "data.results",
        columnsMap: columnsMap,
        rowClickHandler: handleTableRowClick
      }}

      pagination={{
        hideLabels: isMobile,
        type: "cursor_based",
        dataLocation: {
          next: "data.next",
          prev: "data.previous"
        },
        isMobile: isMobile
      }}

      queries={{
        queryKeys: queryKeys,
        queryFunction: getDisputes,
        queryFunctionParams: queryFunctionParams,
        onLoadingChange: (isLoading) => setIsTableLoading(isLoading),
      }}

      emptyState={{
        text: t('innerLayout.disputesPage.table.emptyState')
      }}
    />
  )
}
